import { FC } from 'react';
import { InfoTable } from '@/components/info-table';
import { InfoTableRowProps } from '@/components/info-table/info-table-row';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { useUserRole } from '@/hooks/use-user-role';
import { DrawerContentLayout } from '@/layouts/drawer-content-layout';
import { AnyDevice, DeviceAssignedToOrganisation } from '@/types/device';
import {
  getDeviceDisplayName,
  getDeviceStatusDisplay,
  getDeviceTypeLabel,
  isAlexaDevice,
  isHexNodeDevice,
} from '@/utils/device-helpers';
import { AlexaFunctionsSection } from './alexa-functions';
import { DeviceSkillsSection } from './device-skills-section';

export type ViewDeviceProps = {
  device: AnyDevice;
  unlinkType: 'organisation' | 'unit';
  onClose: () => void;
  onDelete?: () => void;
  onUnlink: () => void;
};

const hasOrganisation = (d: AnyDevice): d is DeviceAssignedToOrganisation =>
  d.organisation !== undefined && d.organisation !== null;

export const ViewDevice: FC<ViewDeviceProps> = ({
  device,
  unlinkType,
  onClose,
  onDelete,
  onUnlink,
}) => {
  const { isPlatformAdmin } = useUserRole();
  const hasUnit = 'unit' in device && device.unit !== undefined;

  const showUnlinkbutton =
    unlinkType === 'organisation' ? hasOrganisation(device) : hasUnit;

  const metaRows: InfoTableRowProps[] = [];
  if (isAlexaDevice(device) && device.meta) {
    metaRows.push({
      label: 'Device Endpoint',
      value: device.meta.deviceEndpoint,
      stack: true,
    });
    if (device.meta.roomEndpoint) {
      metaRows.push({
        label: 'Room Endpoint',
        value: device.meta.roomEndpoint,
        stack: true,
      });
    }
  } else if (isHexNodeDevice(device) && device.meta) {
    metaRows.push({
      label: 'Tablet ID',
      value: device.meta.tabletId,
      stack: true,
    });
  }

  const showSkills = isAlexaDevice(device) && hasOrganisation(device);
  const showAlexaFunctions =
    isPlatformAdmin && isAlexaDevice(device) && Boolean(device.meta);

  // Tablets derive status from lastSeen and may have no connectivity object.
  const showStatus = isHexNodeDevice(device) || !!device.state?.connectivity;
  const statusDisplay = getDeviceStatusDisplay(device);

  return (
    <DrawerContentLayout
      buttonItems={[
        showUnlinkbutton && (
          <Button key="unlink" onClick={onUnlink} variant="secondary-warning">
            {unlinkType === 'organisation'
              ? strings.devices.drawer_view.unlink_organisation
              : strings.devices.drawer_view.unlink_unit}
          </Button>
        ),
        onDelete && (
          <Button key="delete" onClick={onDelete} variant="secondary-warning">
            {strings.devices.drawer_view.delete}
          </Button>
        ),
        <div className="grow" key="flex"></div>,
        <Button key="close" onClick={onClose} variant="secondary">
          {strings.devices.drawer_view.close}
        </Button>,
      ]}
      mainContent={
        <>
          <InfoTable
            rows={
              [
                { label: 'Device name', value: getDeviceDisplayName(device) },
                { label: 'Type', value: getDeviceTypeLabel(device.type) },
                device.meta?.serialNumber && {
                  label: 'Device serial number (DSN)',
                  value: device.meta.serialNumber,
                },
                device.meta?.model && {
                  label: 'Model',
                  value: device.meta.model,
                },
                {
                  label: 'Assigned organisation',
                  value: hasOrganisation(device)
                    ? device.organisation.name
                    : 'Not Assigned',
                  valueClassName: hasOrganisation(device)
                    ? undefined
                    : 'italic',
                },
                ...(unlinkType === 'unit'
                  ? [
                      {
                        label: 'Associated unit',
                        value: hasUnit
                          ? device.unit?.unitName
                          : 'Non-associated',
                        valueClassName: hasUnit ? undefined : 'italic',
                      },
                    ]
                  : []),
                ...metaRows,
                showStatus && {
                  label: 'Connection status',
                  value: (
                    <StatusBadge
                      text={statusDisplay.text}
                      variant={statusDisplay.variant}
                    />
                  ),
                },
              ].filter(Boolean) as InfoTableRowProps[]
            }
            title="Device Information"
          />
          {showSkills && <DeviceSkillsSection skills={device.state?.skills} />}
          {showAlexaFunctions && <AlexaFunctionsSection device={device} />}
        </>
      }
    />
  );
};
